import React, { useEffect, useState, useMemo } from "react";
import { CreditCard, Banknote, Check, X, RefreshCw, Eye } from "lucide-react";
import { adminPendingPayments, adminApprovePayment, adminRejectPayment } from "../../paymentsApi";
import { useToast } from "../../hooks/use-toast";

const STATUS_LABELS = {
  pending: { label: "Pendiente", color: "bg-amber-100 text-amber-700" },
  pending_review: { label: "Esperando revisión", color: "bg-purple-100 text-purple-700" },
  credited: { label: "Acreditado", color: "bg-emerald-100 text-emerald-700" },
  approved: { label: "Aprobado", color: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rechazado", color: "bg-red-100 text-red-700" },
  expired: { label: "Expirado", color: "bg-gray-100 text-gray-700" },
};

export default function AdminPayments() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [proofView, setProofView] = useState(null);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminPendingPayments();
      setItems(data || []);
    } catch (e) {
      toast({ title: "Error", description: "No pudimos cargar los pagos." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    if (filter === "zelle") return items.filter((t) => t.method === "zelle");
    if (filter === "stripe") return items.filter((t) => t.method === "stripe");
    if (filter === "pending") return items.filter((t) => ["pending_review", "pending"].includes(t.status));
    return items;
  }, [items, filter]);

  const approve = async (id) => {
    try {
      await adminApprovePayment(id);
      toast({ title: "Aprobado", description: "Créditos acreditados al usuario." });
      load();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "No pudimos aprobar." });
    }
  };
  const reject = async (id) => {
    if (!window.confirm("¿Rechazar esta transacción? El usuario no recibirá créditos.")) return;
    try {
      await adminRejectPayment(id);
      toast({ title: "Rechazado" });
      load();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "No pudimos rechazar." });
    }
  };

  return (
    <div className="bg-white rounded-3xl shadow-md border border-blue-100 p-6 sm:p-8">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="text-2xl font-extrabold text-[#0e4a7a]">Pagos</h2>
          <p className="text-sm text-gray-500">Stripe (tarjetas) y Zelle (revisión manual).</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-[#1d6cb1] font-semibold text-sm border border-blue-100 hover:bg-blue-100">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualizar
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {[
          { v: "all", l: "Todos" },
          { v: "pending", l: "Pendientes" },
          { v: "zelle", l: "Zelle" },
          { v: "stripe", l: "Tarjeta" },
        ].map((opt) => (
          <button
            key={opt.v}
            onClick={() => setFilter(opt.v)}
            data-testid={`pay-filter-${opt.v}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition border ${
              filter === opt.v
                ? "bg-[#1d6cb1] text-white border-transparent shadow"
                : "bg-white text-[#1d6cb1] border-blue-200 hover:bg-blue-50"
            }`}
          >
            {opt.l}
          </button>
        ))}
      </div>

      {loading && items.length === 0 ? (
        <p className="text-center text-gray-500 py-10">Cargando...</p>
      ) : filtered.length === 0 ? (
        <p className="text-center text-gray-500 py-10">No hay transacciones.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-gray-500 border-b border-blue-100">
              <tr>
                <th className="py-2 pr-3">Método</th>
                <th className="py-2 pr-3">Usuario</th>
                <th className="py-2 pr-3">Paquete</th>
                <th className="py-2 pr-3">Monto</th>
                <th className="py-2 pr-3">Referencia</th>
                <th className="py-2 pr-3">Estado</th>
                <th className="py-2 pr-3">Fecha</th>
                <th className="py-2 pr-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => {
                const s = STATUS_LABELS[t.status] || { label: t.status, color: "bg-gray-100 text-gray-700" };
                const isReviewable = t.status === "pending_review";
                return (
                  <tr key={t.id} className="border-b border-blue-50 hover:bg-blue-50/30">
                    <td className="py-3 pr-3">
                      <span className="inline-flex items-center gap-1 text-xs font-bold">
                        {t.method === "zelle" ? <Banknote className="w-3.5 h-3.5 text-purple-600" /> : <CreditCard className="w-3.5 h-3.5 text-blue-600" />}
                        {t.method.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3 pr-3">
                      <p className="font-semibold text-gray-900">{t.user_email}</p>
                      {t.method === "zelle" && t.sender_name && (
                        <p className="text-[11px] text-gray-500">Envió: {t.sender_name}</p>
                      )}
                    </td>
                    <td className="py-3 pr-3">
                      <span className="font-mono text-xs">{t.package_id}</span>
                      <p className="text-xs text-emerald-600 font-bold">+{t.credits} créd.</p>
                    </td>
                    <td className="py-3 pr-3 font-bold">${t.amount} {t.currency || "USD"}</td>
                    <td className="py-3 pr-3 font-mono text-xs text-gray-700">{t.reference || t.session_id?.slice(-8) || "-"}</td>
                    <td className="py-3 pr-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${s.color}`}>
                        {s.label}
                      </span>
                    </td>
                    <td className="py-3 pr-3 text-xs text-gray-500">{(t.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-1.5">
                        {t.proof_data_url && (
                          <button
                            onClick={() => setProofView(t)}
                            className="p-1.5 rounded-lg bg-blue-50 text-[#1d6cb1] hover:bg-blue-100"
                            title="Ver comprobante"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        {isReviewable && (
                          <>
                            <button
                              data-testid={`approve-${t.id}`}
                              onClick={() => approve(t.id)}
                              className="p-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                              title="Aprobar"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              data-testid={`reject-${t.id}`}
                              onClick={() => reject(t.id)}
                              className="p-1.5 rounded-lg bg-red-100 text-red-700 hover:bg-red-200"
                              title="Rechazar"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Proof viewer */}
      {proofView && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setProofView(null)}>
          <div className="bg-white rounded-2xl p-4 max-w-2xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500">Comprobante de</p>
                <p className="font-extrabold text-[#0e4a7a]">{proofView.sender_name} · ${proofView.amount} {proofView.currency || "USD"}</p>
                <p className="text-xs text-gray-500">Ref: {proofView.reference}</p>
                {proofView.note && <p className="text-xs text-gray-700 mt-1">{proofView.note}</p>}
              </div>
              <button onClick={() => setProofView(null)} className="p-2 rounded-full hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>
            <img src={proofView.proof_data_url} alt="Comprobante" className="w-full max-h-[70vh] object-contain rounded-xl" />
          </div>
        </div>
      )}
    </div>
  );
}
